import { jwtDecode } from "jwt-decode";

import { type User, type DeveloperProfile } from "../client/svcDirectory";
import { switchAuthScopes } from "./authService";
import { removeUsingCognitoCookies } from "./redirectStorage";
import { getIdpAdminClient } from "../client/svcClients";
import { AdminAppRoutes } from "../constants/routes";

export const REFRESH_TOKEN_PERIOD = 12 * 60 * 1000;

const CURRENT_SELECTED_TENANT = "currentSelectedTenant";
const IMPERSONATED_EMAIL = "impersonatedEmail";
const IS_SJ_ADMIN = "isSJAdmin";
const REDIRECTED_TO_ADMIN = "redirectedToAdmin";
const SJ_ADMIN_RETURN_URL = "sjAdminReturnUrl";

export const cleanUserArtifacts = () => {
  localStorage.removeItem("idToken");
  localStorage.removeItem("meta");
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("isImpersonating");
  removeCurrentSelectedTenant();
  removeImpersonatedEmail();
  removeIsSjAdminFromStorage();
  removeRedirectedToAdminFromStorage();
  removeUsingCognitoCookies();
};

export const removeCurrentSelectedTenant = () => localStorage.removeItem(CURRENT_SELECTED_TENANT);
export const removeImpersonatedEmail = () => localStorage.removeItem(IMPERSONATED_EMAIL);
export const removeIsSjAdminFromStorage = () => localStorage.removeItem(IS_SJ_ADMIN);
export const removeRedirectedToAdminFromStorage = () =>
  sessionStorage.removeItem(REDIRECTED_TO_ADMIN);

export const getAuthenticated = () => !!localStorage.getItem("authToken");
export const getAuthToken = () => localStorage.getItem("authToken");
export const getIsImpersonating = () => localStorage.getItem("isImpersonating");
export const getCurrentSelectedTenant = () => localStorage.getItem(CURRENT_SELECTED_TENANT);
export const getImpersonatedEmail = () => localStorage.getItem(IMPERSONATED_EMAIL);
export const getIsSjAdminFromStorage = () => localStorage.getItem(IS_SJ_ADMIN) === "true";

export const saveSJAdminReturnUrl = (returnUrl: string) =>
  sessionStorage.setItem(SJ_ADMIN_RETURN_URL, returnUrl);
export const getSJAdminReturnUrl = () => sessionStorage.getItem(SJ_ADMIN_RETURN_URL);
export const removeSJAdminReturnUrl = () => sessionStorage.removeItem(SJ_ADMIN_RETURN_URL);

export const idTokenExpired = () => {
  const token = getIdToken();
  if (token) {
    const claims = jwtDecode(token);
    if (claims.exp) {
      return claims.exp < Math.floor(Date.now() / 1000);
    }
  }
  return false;
};

export const getRefreshToken = () => localStorage.getItem("refreshToken");
export const getIdToken = () => localStorage.getItem("authToken");

export const saveSelectedTenant = (tenantName: string) =>
  localStorage.setItem(CURRENT_SELECTED_TENANT, tenantName);

export const saveImpersonatedEmail = (email: string) =>
  localStorage.setItem(IMPERSONATED_EMAIL, email);

export const setIsSjAdminToStorage = () => localStorage.setItem(IS_SJ_ADMIN, "true");

// Get role from User's tenants array, returns boolean
export const mapIsAdmin = (user: User, currentTenant: string) =>
  user.tenants?.some(
    ({ tenant, roles }) => tenant === currentTenant && roles?.includes("tenant_admin")
  );

// Check if user is archived, returns boolean
export const mapIsArchived = (
  directoryUser: User,
  railsUser: { archived?: boolean },
  currentTenant: string
): boolean =>
  Boolean(
    directoryUser.tenants?.some(tenant => tenant.tenant === currentTenant && tenant.deactivated) ||
    railsUser.archived
  );

// Manage a tenant by saving the selected tenant and switching auth scopes to include both the tenant and sj-admin scopes
export const manageTenant = async (tenantName: string) => {
  saveSelectedTenant(tenantName);
  await switchAuthScopes({ admin: tenantName, sjAdmin: true });
};

// Exit managing a tenant by removing the selected tenant and switching auth scopes back to sj-admin only
export const exitManageTenant = async () => {
  removeCurrentSelectedTenant();

  await switchAuthScopes({ sjAdmin: true });
};

// Let SJ admin visit a tenant
export const visitTenant = async (tenantName: string) => {
  saveSelectedTenant(tenantName);
  await switchAuthScopes({ admin: tenantName });
};

// Let SJ admin visit a tenant and redirect to the Admin App
export const visitTenantAndRedirect = async (tenantName: string, returnUrl?: string) => {
  await visitTenant(tenantName);
  if (returnUrl) {
    saveSJAdminReturnUrl(returnUrl);
  }

  window.location.assign(AdminAppRoutes.route);
};

// Resolve a stored return URL to a safe same-origin path, or null if it points
// elsewhere. Guards against open redirects from a tampered sessionStorage value
// (e.g. "https://evil.com", "//evil.com", "javascript:...") by only allowing
// same-origin targets and returning a path rebuilt from the parsed URL.
export const toSafeReturnPath = (returnUrl: string): string | null => {
  try {
    const url = new URL(returnUrl, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

// Taking SJ admin out of a tenant context and back to the SJ Admin App
export const exitTenantVisit = async () => {
  removeCurrentSelectedTenant();
  removeImpersonatedEmail();

  await switchAuthScopes({ sjAdmin: true });
  const returnUrl = getSJAdminReturnUrl();
  if (returnUrl) {
    removeSJAdminReturnUrl();
  }

  const safePath = returnUrl ? toSafeReturnPath(returnUrl) : null;
  window.location.assign(safePath ?? AdminAppRoutes.route);
};

// Let SJ admin impersonate a tenant admin user
export const impersonateAsync = async (email: string) => {
  saveImpersonatedEmail(email);
  await switchAuthScopes({ as: email });
  window.location.href = "/"; // Go to the platform home page
};

// Let SJ admin stop impersonating a tenant admin user
export const stopImpersonating = async (tenantName: string) => {
  await switchAuthScopes({ sjAdmin: true, admin: tenantName });
  removeImpersonatedEmail();
};

export const isDeveloperProfileEmpty = (profile?: DeveloperProfile) => {
  if (!profile) return true;

  return Object.values(profile).every(
    value => value === "" || (Array.isArray(value) && value.length === 0)
  );
};

export const getTenantUuid = async (): Promise<string | null> => {
  try {
    const tenantName = getCurrentSelectedTenant();

    if (!tenantName) return null;

    const idpAdminClient = getIdpAdminClient();
    const { data, error } = await idpAdminClient.getTenantPrimaryIdp(tenantName);

    if (error) {
      console.error("Error fetching tenant IDP:", error);
      return null;
    }

    return data?.idp?.uuid || null;
  } catch (error) {
    console.error("Error fetching tenant UUID:", error);
    return null;
  }
};
